import React, { useState } from "react";
import { IUserDetails } from "../../exports/interfaces";
import useMobile from "../../hooks/useMobile";
import Select from "react-select";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { Upload } from "lucide-react";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useOption } from "../../context/OptionContext";
import { renderFilePreview } from "../../exports/functions";
import { useRole } from "../../context/RoleContext";

const UserAdd = () => {
  const isMobile = useMobile();
  const { statusOptions } = useOption();
  const { setLoading } = useLoader();
  const { roles } = useRole();
  const [userInfo, setUserInfo] = useState<IUserDetails>({
    userName: "",
    mobileNumber: "",
    email: "",
    password: "",
    nidNumber: "",

    companyName: "",
    department: "",
    position: "",

    userRole: "",
    userStatus: "",
    fileName: "",
    file: null,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUserInfo((prev) => ({
        ...prev,
        fileName: file.name,
        file: file,
      }));
    }
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setUserInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const positionOptions = [
    { value: "CEO", label: "CEO" },
    { value: "Chief Financial Officer", label: "Chief Financial Officer" },
    {
      value: "Chief Administration Officer",
      label: "Chief Administration Officer",
    },
    { value: "Registration Officer", label: "Registration Officer" },
    { value: "Manager", label: "Manager" },
    { value: "IT Officer", label: "IT Officer" },
  ];

  const userRoleOptions =
    roles?.map((role) => ({
      value: role.label,
      label: role.label,
    })) || [];

  const userStatusOptions =
    statusOptions
      ?.find((item) => item.name === "User Statuses")
      ?.statusOptions?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const handleSelectChange = (
    name: keyof IUserDetails,
    selected: SelectOption | null,
  ) => {
    setUserInfo((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("userName", userInfo.userName);
      payload.append("mobileNumber", userInfo.mobileNumber);
      payload.append("email", userInfo.email);
      payload.append("password", userInfo.password || "");
      payload.append("nidNumber", userInfo.nidNumber);

      payload.append("companyName", userInfo.companyName);
      payload.append("department", userInfo.department);
      payload.append("position", userInfo.position);

      payload.append("userRole", userInfo.userRole);
      payload.append("userStatus", userInfo.userStatus);

      if (userInfo.file) {
        payload.append("documentUrl", userInfo.file);
      }

      const response = await axiosClient.post("/users/register", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        setUserInfo({
          userName: "",
          mobileNumber: "",
          email: "",
          password: "",
          nidNumber: "",

          companyName: "",
          department: "",
          position: "",

          userRole: "",
          userStatus: "",
          fileName: "",
          file: null,
        });

        dynamicSuccess("User created successfully!");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Add New User</h3>
          <small>Record a new User</small>
        </div>
      </div>
      <div className={`dashboard-card`}>
        <div className="row g-3">
          <div className="col-12">
            <div className="dashboard-card">
              <h5 className="text-uppercase mb-4">USER INFORMATION</h5>
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label">
                    User Name <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Name here"
                    name="userName"
                    value={userInfo.userName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Mobile Number <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter Mobile number here"
                    name="mobileNumber"
                    value={userInfo.mobileNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Email Address <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Email Address here"
                    name="email"
                    value={userInfo.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Password <span className="primary-color">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter password here"
                    name="password"
                    value={userInfo.password}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    NID Number <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter NID here"
                    name="nidNumber"
                    value={userInfo.nidNumber}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="dashboard-card mt-2">
              <h5 className="text-uppercase mb-4">User Details</h5>
              <div className="row g-4">
                <div className="col-md-4">
                  <label className="form-label">
                    Company Name <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    className="form-control  "
                    placeholder="Enter Street\tole. here"
                    onChange={handleChange}
                    value={userInfo.companyName}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Department <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    name="department"
                    className="form-control  "
                    placeholder="Enter Administrator email here"
                    onChange={handleChange}
                    value={userInfo.department}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Position <span className="primary-color">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    options={positionOptions}
                    placeholder="Select Position"
                    value={
                      positionOptions.find(
                        (opt) => opt.value === userInfo.position,
                      ) || null
                    }
                    onChange={(selected) =>
                      handleSelectChange("position", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    User Role <span className="primary-color">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    options={userRoleOptions}
                    placeholder="User Role"
                    value={
                      userRoleOptions.find(
                        (opt) => opt.value === userInfo.userRole,
                      ) || null
                    }
                    onChange={(selected) =>
                      handleSelectChange("userRole", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    User Status <span className="primary-color">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    options={userStatusOptions}
                    placeholder="User Status"
                    value={
                      userStatusOptions.find(
                        (opt) => opt.value === userInfo.userStatus,
                      ) || null
                    }
                    onChange={(selected) =>
                      handleSelectChange("userStatus", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Upload Image (PDF, JPEG, PNG)
                  </label>
                  <div
                    className={`upload-box p-4 ${!userInfo.file && "text-center"}`}
                  >
                    {userInfo.file ? (
                      renderFilePreview(
                        userInfo.file,
                        userInfo.fileName,
                        (e: any) => handleFileChange(e),
                      )
                    ) : (
                      <>
                        <Upload />

                        <p className="mb-1">
                          Choose a file or drag and drop it here.
                        </p>

                        <small className="d-block mb-3">
                          (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                        </small>

                        <label className="btn btn-badge text-main-color">
                          Browse file
                          <input
                            hidden
                            type="file"
                            onChange={(e) => {
                              handleFileChange(e);
                            }}
                          />
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`d-flex mt-4 justify-content-end ${isMobile ? "flex-wrap gap-2" : ""}`}
        >
          <div className="d-flex gap-2">
            <button className="btn px-3 btn-badge-border">Cancel</button>
            <button
              className="btn btn-badge-primary px-3"
              onClick={handleSubmit}
            >
              Save User
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserAdd;
